interface Owner {
  source?: string
  sourceContactID?: string
  [key: string]: unknown
}

interface PropertyMeOrder {
  isSortable: boolean
  groupKey?: string
  sequence: number
}

interface PropertyMeOwnerSlot<T extends Owner> {
  owner: T
  index: number
  propertyMeOrder: PropertyMeOrder
}

const PROPERTY_ME_SOURCE = 'PropertyMe.com'

const buildPropertyMeSourceIdSet = (owners: Owner[]): Set<string> => {
  return new Set(
    owners
      .filter((owner) => owner?.source === PROPERTY_ME_SOURCE)
      .map((owner) => owner?.sourceContactID)
      .filter((id): id is string => Boolean(id)),
  )
}

const extractPropertyMeOrder = (
  sourceContactID: string | undefined,
  propertyMeSourceIDs: Set<string>,
): PropertyMeOrder => {
  if (typeof sourceContactID !== 'string' || sourceContactID.length === 0) {
    return {
      isSortable: false,
      sequence: Number.MAX_SAFE_INTEGER,
    }
  }

  const secondaryMatch = sourceContactID.match(/-(\d+)$/)
  if (!secondaryMatch) {
    return {
      isSortable: true,
      groupKey: sourceContactID,
      sequence: 0,
    }
  }

  const baseSourceContactID = sourceContactID.slice(
    0,
    sourceContactID.length - secondaryMatch[0].length,
  )
  if (!propertyMeSourceIDs.has(baseSourceContactID)) {
    return {
      isSortable: true,
      groupKey: sourceContactID,
      sequence: 0,
    }
  }

  return {
    isSortable: true,
    groupKey: baseSourceContactID,
    sequence: Number(secondaryMatch[1]) + 1,
  }
}

export const sortPropertyMeOwnersForDisplay = <T extends Owner>(
  owners: T[] = [],
): T[] => {
  if (!Array.isArray(owners) || owners.length < 2) {
    return owners
  }

  const propertyMeSourceIDs = buildPropertyMeSourceIdSet(owners)
  const propertyMeOwnerSlots: PropertyMeOwnerSlot<T>[] = owners
    .map((owner, index) => ({
      owner,
      index,
      propertyMeOrder:
        owner?.source === PROPERTY_ME_SOURCE
          ? extractPropertyMeOrder(owner?.sourceContactID, propertyMeSourceIDs)
          : {
              isSortable: false,
              sequence: Number.MAX_SAFE_INTEGER,
            },
    }))
    .filter(({ owner }) => owner?.source === PROPERTY_ME_SOURCE)

  if (propertyMeOwnerSlots.length < 2) {
    return owners
  }

  const reorderedOwners = [...owners]

  const propertyMeOwnerGroups = propertyMeOwnerSlots.reduce((groups, slot) => {
    if (!slot.propertyMeOrder.isSortable || !slot.propertyMeOrder.groupKey) {
      return groups
    }

    const group = groups.get(slot.propertyMeOrder.groupKey) || []
    group.push(slot)
    groups.set(slot.propertyMeOrder.groupKey, group)
    return groups
  }, new Map<string, PropertyMeOwnerSlot<T>[]>())

  propertyMeOwnerGroups.forEach((group) => {
    if (group.length < 2) {
      return
    }

    const targetIndices = group.map(({ index }) => index)
    const sortedGroupOwners = [...group]
      .sort((left, right) => {
        const sequenceDelta =
          left.propertyMeOrder.sequence - right.propertyMeOrder.sequence
        if (sequenceDelta !== 0) {
          return sequenceDelta
        }

        return left.index - right.index
      })
      .map(({ owner }) => owner)

    targetIndices.forEach((index, groupIndex) => {
      const owner = sortedGroupOwners[groupIndex]
      if (owner !== undefined) {
        reorderedOwners[index] = owner
      }
    })
  })

  return reorderedOwners
}
